import React, { useMemo, useState } from "react";
import { useFormikContext } from "formik";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import ScannerInput from "./ScannerInput";
import SerialNumbersEditorModal from "./SerialNumbersEditorModal";

const splitSerials = value =>
  String(value || "")
    .split(",")
    .map(v => v.trim())
    .filter(Boolean);

const joinSerials = serials => serials.map(v => String(v || "").trim()).filter(Boolean).join(", ");

const chipStyles = {
  minWidth: 24,
  height: 20,
  fontWeight: 500,
  fontSize: "0.7rem",
  color: "text.secondary",
  borderColor: "grey.400",
  backgroundColor: "transparent",
  "& .MuiChip-label": { px: 0.75 },
};

export default function SerialNumberField({
  name,
  label,
  disabled,
  multiScanLabel = "Serial Number",
  ...props
}) {
  const { values, setFieldValue } = useFormikContext();
  const [modalOpen, setModalOpen] = useState(false);

  const serialValue = values?.[name] || "";
  const serials = useMemo(() => splitSerials(serialValue), [serialValue]);
  const hasMultipleSerials = serials.length > 1;
  const isEditable = hasMultipleSerials && !disabled;

  if (!hasMultipleSerials) {
    return (
      <ScannerInput
        {...props}
        name={name}
        label={label}
        disabled={disabled}
        enableMultiScanFlow
        multiScanLabel={multiScanLabel}
      />
    );
  }

  return (
    <>
      <Box
        onClick={() => {
          if (isEditable) setModalOpen(true);
        }}
        sx={{
          border: theme => `1px solid ${theme.palette.mode === "light" ? "rgba(0,0,0,0.23)" : "rgba(255,255,255,0.23)"}`,
          borderRadius: theme => `${theme.shape.borderRadius}px`,
          backgroundColor: "white",
          px: 1.75,
          pt: 2,
          pb: 1,
          cursor: isEditable ? "pointer" : "default",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          pr: isEditable ? 5 : 1.75,
          "&:hover": isEditable
            ? { borderColor: "text.primary" }
            : undefined,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: 0,
            left: 14,
            transform: "translateY(-50%)",
            px: 0.75,
            backgroundColor: "white",
            color: "text.secondary",
            fontSize: "0.95rem",
            lineHeight: 1.1,
          }}
        >
          {label}
        </Typography>

        {serials.map((serial, index) => (
          <Box
            key={`${serial}-${index}`}
            sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 24 }}
          >
            <Chip label={index + 1} size="small" variant="outlined" sx={chipStyles} />
            <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
              {serial}
            </Typography>
          </Box>
        ))}

        {isEditable && (
          <Box
            sx={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <Tooltip title="Click to view details">
              <InfoOutlinedIcon color="primary" fontSize="small" style={{ cursor: "pointer" }} />
            </Tooltip>
          </Box>
        )}
      </Box>
      <SerialNumbersEditorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={label || multiScanLabel}
        fieldLabel={multiScanLabel}
        addButtonLabel={`+ Add ${multiScanLabel}`}
        initialSerials={serials}
        onSave={nextSerials => {
          setFieldValue(name, joinSerials(nextSerials));
        }}
      />
    </>
  );
}
